/**
 * "Visible to departments" — a Library document's (an article's too) or a
 * folder's departments, set where its other properties are edited.
 *
 * Library LB6-T8, acceptance L6-08. Decision 31 (B10): an item's departments
 * are set in its EDIT FORM and in FOLDER SETTINGS with the Users rail's
 * department-scope picker — never in a Share dialog (a share's Department tab
 * names a department as a share SUBJECT; that is a different control, and it
 * stays where it is). Decision 23 (B4): a document with no departments of its
 * own follows its nearest scoped folder; that inheritance is decided once, in
 * the api's Document visibility resolver, which is the reader of what this
 * field writes.
 *
 * ONE PICKER. The control is `DepartmentScopePicker` from
 * components/users/department-scope-picker.tsx, imported — the same component
 * the Users rail sets a person's departments with. There is no Library copy.
 *
 * ITS KEYS (OQ-L6-2, founder 2026-10-22). The field decides who may see the
 * item, so it asks share:library — the key `PUT /library/acl/{documents,
 * folders}/:id/departments` declares. It draws its choices from the tenant's
 * department list, `GET /users/departments`, which declares read:users; a
 * field that cannot list the departments can never be used, so it asks that key
 * too (LB6-T8 review R4). It is not drawn for a person who lacks either. A
 * person who may edit a document's metadata but not share it sees the edit
 * form without this field. Both keys are recorded on the field's rows in
 * LIBRARY_CONTROLS (@argusai/shared), `alsoAsks`.
 *
 * THE WIRE. GET / PUT `/library/acl/{documents,folders}/:id/departments`
 * carry `{ companyWide, departmentIds }` (ItemDepartmentScopeSchema in
 * @argusai/shared). The picker edits `{ all, ids }`; "All departments" is
 * company-wide — zero scope rows, which for an item in a scoped folder means
 * "follows its folder", and the hint under the field says so.
 *
 * NOTHING FAILS OPEN. Until the item's current departments are read, the field
 * shows that it is loading (or that the read failed, with a retry) and the
 * form saves no departments — the item keeps the ones it has. A scope with no
 * department ticked and "All departments" off is refused before the request,
 * with the same sentence the Users rail uses, as the api refuses it.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  DepartmentScopePicker,
  INCOMPLETE_SCOPE_MESSAGE,
  isCompleteScope,
  sameScope,
  type DepartmentScopeValue,
} from '@/components/users/department-scope-picker';
import { useAllDepartments } from '@/lib/use-departments';
import { useHasPermission } from '@/lib/use-has-permission';
import { apiClient } from '@/lib/api-client';
import {
  ItemDepartmentScopeSchema,
  type ItemDepartmentScope,
  type ItemDepartmentScopeWrite,
} from '@argusai/shared/schemas/departments';

/** The two kinds of Library item that carry departments. An article is a document. */
export type ItemDepartmentsKind = 'document' | 'folder';

/** A new item (an article not yet saved) has no departments of its own. */
export const UNSCOPED_ITEM: DepartmentScopeValue = { all: true, ids: [] };

/** The api's answer, as the picker edits it. */
export function scopeFromItem(scope: ItemDepartmentScope): DepartmentScopeValue {
  return scope.companyWide ? { all: true, ids: [] } : { all: false, ids: [...scope.departmentIds] };
}

/** The picker's value, as the PUT body (ItemDepartmentScopeWriteSchema). */
export function itemScopeBody(value: DepartmentScopeValue): ItemDepartmentScopeWrite {
  return value.all
    ? { companyWide: true, departmentIds: [] }
    : { companyWide: false, departmentIds: [...new Set(value.ids)] };
}

export function itemDepartmentsQueryKey(kind: ItemDepartmentsKind, id: string | null): readonly unknown[] {
  return ['library', 'item-departments', kind, id];
}

async function fetchItemDepartments(kind: ItemDepartmentsKind, id: string): Promise<ItemDepartmentScope> {
  const res =
    kind === 'document'
      ? await apiClient.get<unknown>(`/library/acl/documents/${id}/departments`)
      : await apiClient.get<unknown>(`/library/acl/folders/${id}/departments`);
  // An answer that is not the scope is a failed read, never "company-wide".
  return ItemDepartmentScopeSchema.parse(res);
}

/**
 * WHAT A SAVE WRITES: the PUT body, or null when there is nothing to write —
 * the person may not set departments, the item's current departments were
 * never read (the form cannot know what it would replace), or the edit is the
 * same scope the item has. An incomplete scope throws: it is refused here, as
 * the api refuses it, rather than sent.
 */
export function itemDepartmentsWrite(state: {
  allowed: boolean;
  loaded: DepartmentScopeValue | null;
  edited: DepartmentScopeValue | null;
}): ItemDepartmentScopeWrite | null {
  if (!state.allowed || state.loaded === null || state.edited === null) return null;
  if (sameScope(state.edited, state.loaded)) return null;
  if (!isCompleteScope(state.edited)) throw new Error(INCOMPLETE_SCOPE_MESSAGE);
  return itemScopeBody(state.edited);
}

/** PUT the item's departments; answers the scope the api stored. */
export async function saveItemDepartments(
  kind: ItemDepartmentsKind,
  id: string,
  body: ItemDepartmentScopeWrite,
): Promise<ItemDepartmentScope> {
  const res =
    kind === 'document'
      ? await apiClient.put<unknown>(`/library/acl/documents/${id}/departments`, body)
      : await apiClient.put<unknown>(`/library/acl/folders/${id}/departments`, body);
  return ItemDepartmentScopeSchema.parse(res);
}

export interface ItemDepartmentsDraft {
  kind: ItemDepartmentsKind;
  /** The person holds share:library and read:users — the field is drawn and may be saved. */
  allowed: boolean;
  /** 'ready' only once the item's current departments are in hand (or it is new). */
  status: 'idle' | 'loading' | 'error' | 'ready';
  /** What the picker shows; null until ready. */
  value: DepartmentScopeValue | null;
  setValue: (next: DepartmentScopeValue) => void;
  /** The person changed the departments from what the item has. */
  dirty: boolean;
  /** The value is one the api accepts (all, or at least one department). */
  complete: boolean;
  retry: () => void;
  /** Forget the edit (Cancel). */
  reset: () => void;
  /**
   * Write the edit, if there is one, to `id` (the item's id — for a new
   * article, the id its create answered). A no-op when nothing changed, the
   * person may not set departments, or the current departments were never
   * read. Rejects with the api's error.
   */
  save: (id: string) => Promise<void>;
}

/**
 * May this person set an item's departments? share:library — the key the
 * departments PUT declares — AND read:users — the key of GET /users/departments,
 * the list the field draws its choices from. One answer for every surface that
 * offers the field (the folder menu's Settings entry included).
 */
export function useCanSetItemDepartments(): boolean {
  const canShare = useHasPermission('share', 'library');
  const canListDepartments = useHasPermission('read', 'users');
  return canShare && canListDepartments;
}

/**
 * The departments field's state, for one item. `id` null is an item not yet
 * created (a new article): it starts with no departments of its own.
 * `enabled` false (the form is not open) reads nothing.
 */
export function useItemDepartmentsDraft({
  kind,
  id,
  enabled = true,
}: {
  kind: ItemDepartmentsKind;
  id: string | null;
  enabled?: boolean;
}): ItemDepartmentsDraft {
  // OQ-L6-2: the key the departments route declares (PUT …/departments →
  // share:library), and read:users for the department list it picks from.
  // Without both the field is not drawn and nothing is read.
  const allowed = useCanSetItemDepartments();
  const queryClient = useQueryClient();
  const [edited, setEdited] = useState<DepartmentScopeValue | null>(null);

  const readsItem = allowed && enabled && id !== null;
  const query = useQuery<ItemDepartmentScope>({
    queryKey: itemDepartmentsQueryKey(kind, id),
    queryFn: () => fetchItemDepartments(kind, id as string),
    enabled: readsItem,
    staleTime: 0,
    retry: false,
  });

  const loaded: DepartmentScopeValue | null =
    id === null ? UNSCOPED_ITEM : query.data && !query.isError ? scopeFromItem(query.data) : null;

  const status: ItemDepartmentsDraft['status'] = !allowed || !enabled
    ? 'idle'
    : id === null
      ? 'ready'
      : query.isError
        ? 'error'
        : loaded
          ? 'ready'
          : 'loading';

  const value = status === 'ready' ? edited ?? loaded : null;
  const dirty = status === 'ready' && edited !== null && loaded !== null && !sameScope(edited, loaded);
  const complete = value === null || isCompleteScope(value);

  return {
    kind,
    allowed,
    status,
    value,
    setValue: (next) => setEdited(next),
    dirty,
    complete,
    // refetch() runs even for a disabled query: a new item (no id) or a
    // closed form has nothing to read again (LB6-T8 review BF-05).
    retry: () => {
      if (readsItem) void query.refetch();
    },
    reset: () => setEdited(null),
    save: async (targetId: string) => {
      const body = itemDepartmentsWrite({ allowed, loaded: status === 'ready' ? loaded : null, edited });
      if (body === null) return;
      const stored = await saveItemDepartments(kind, targetId, body);
      queryClient.setQueryData(itemDepartmentsQueryKey(kind, targetId), stored);
      setEdited(null);
    },
  };
}

/** The hint under the field: what "All departments" means for this kind of item. */
export function itemDepartmentsHint(kind: ItemDepartmentsKind): string {
  return kind === 'folder'
    ? 'Only people in the chosen departments see this folder’s documents. With “All departments”, it follows the folder above it, or everyone may see them.'
    : 'Only people in the chosen departments see this document. With “All departments”, it follows its folder’s departments, or everyone may see it.';
}

export const ITEM_DEPARTMENTS_ERROR_TEXT = 'Its departments could not be loaded, so they stay as they are.';

/**
 * The field: a label, the ONE picker, and the hint. Drawn only for a person
 * who holds share:library and read:users (`draft.allowed`); the caller draws
 * it inside the form it belongs to.
 */
export function ItemDepartmentsField({
  draft,
  disabled,
  showLabel = true,
}: {
  draft: ItemDepartmentsDraft;
  disabled?: boolean;
  /** The surface supplies its own heading (a card title) when false. */
  showLabel?: boolean;
}) {
  if (!draft.allowed) return null;
  return (
    <div className="space-y-1.5" data-testid={`item-departments-${draft.kind}`}>
      {showLabel ? <Label className="text-xs font-medium">Visible to departments</Label> : null}
      <ItemDepartmentsControl draft={draft} disabled={disabled} />
      <p className="text-[11px] text-muted-foreground">{itemDepartmentsHint(draft.kind)}</p>
    </div>
  );
}

function ItemDepartmentsControl({ draft, disabled }: { draft: ItemDepartmentsDraft; disabled?: boolean }) {
  // Every department of the tenant — the list the Users rail's pickers write
  // from (a picker that cannot show a department silently narrows the choice).
  const { departments, isLoading, error, truncated, refetch } = useAllDepartments();

  if (draft.status === 'error' || error) {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-x-2 text-xs text-destructive" data-testid="item-departments-error">
        <span>{ITEM_DEPARTMENTS_ERROR_TEXT}</span>
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-xs"
          onClick={() => { draft.retry(); refetch(); }}
          title="Load the departments again"
        >
          Retry
        </Button>
      </div>
    );
  }
  if (draft.status !== 'ready' || draft.value === null || isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="item-departments-loading">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading departments…
      </div>
    );
  }
  return (
    <>
      <DepartmentScopePicker
        value={draft.value}
        onChange={draft.setValue}
        departments={departments.map((d) => ({ id: d.id, name: d.name }))}
        disabled={disabled}
        aria-label="Visible to departments"
      />
      {truncated ? (
        <p className="text-[11px] text-amber-600 dark:text-amber-400">
          Not every department could be listed; the list is incomplete.
        </p>
      ) : null}
    </>
  );
}
